import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Briefcase, UserPlus, CheckCheck } from "lucide-react";
import api from "../lib/api";

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const loadCount = () => api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {});

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      try {
        const r = await api.get("/notifications");
        setItems(r.data);
      } catch {}
    }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((it) => it.map((x) => ({ ...x, read: true })));
    setCount(0);
  };

  const openItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      setCount((c) => Math.max(0, c - 1));
      setItems((it) => it.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle} className="relative w-9 h-9 flex items-center justify-center hover:text-[#002FA7]">
        <Bell size={19} />
        {count > 0 && (
          <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#FF3B30] text-white text-[10px] font-mono2 font-bold flex items-center justify-center rounded-full">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white border border-[#0A0A0A] shadow-[6px_6px_0_0_#0A0A0A] z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] sticky top-0 bg-white">
            <span className="font-display font-bold text-sm">Notifications</span>
            <button data-testid="notif-mark-all" onClick={markAll} className="font-mono2 text-[11px] uppercase text-[#002FA7] hover:underline inline-flex items-center gap-1">
              <CheckCheck size={12} /> Mark all read
            </button>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#9CA3AF] font-mono2">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[#F3F4F6] hover:bg-[#F3F4F6] ${n.read ? "" : "bg-[#002FA7]/5"}`}
              >
                <div className="mt-0.5 text-[#002FA7]">{n.type === "applicant" ? <UserPlus size={16} /> : <Briefcase size={16} />}</div>
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {n.title}
                    {!n.read && <span className="w-1.5 h-1.5 bg-[#002FA7] rounded-full" />}
                  </div>
                  <div className="text-xs text-[#4B5563]">{n.body}</div>
                  <div className="font-mono2 text-[10px] text-[#9CA3AF] mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
